import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

export function DataTable({ columns, rows = [], total = 0, page = 1, pageSize = 25, onPage, onPageSize, sort, dir, onSort, rowKey, onRowClick, loading, emptyMessage = "No records.", search, onSearch, searchPlaceholder = "Search…", toolbar, testId = "data-table" }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="tqi-card overflow-hidden" data-testid={testId}>
      {(onSearch || toolbar) && (
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-4 py-3">
          {onSearch && <Input value={search || ""} onChange={(e) => onSearch(e.target.value)} placeholder={searchPlaceholder} className="max-w-xs" data-testid="table-search-input" />}
          {toolbar}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={cn("px-4 py-3 text-left text-xs font-mono font-semibold uppercase tracking-wider text-slate-600 whitespace-nowrap", c.sortable && "cursor-pointer select-none hover:text-[#101C47]", c.className)}
                  onClick={() => c.sortable && onSort && onSort(c.sortKey || c.key, sort === (c.sortKey || c.key) && dir === "asc" ? "desc" : "asc")} data-testid={`table-header-${c.key}`}>
                  <span className="inline-flex items-center gap-1">{c.label}{c.sortable && (sort === (c.sortKey || c.key) ? (dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-slate-500" data-testid="table-loading">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-slate-500" data-testid="table-empty">{emptyMessage}</td></tr>}
            {rows.map((row, i) => {
              const id = rowKey ? rowKey(row) : row.id || i;
              return (
                <tr key={id} data-testid={`table-row-${id}`} onClick={() => onRowClick && onRowClick(row)} className={cn("border-t border-slate-100 transition-colors", onRowClick && "cursor-pointer hover:bg-[#EEF2F9]")}>
                  {columns.map((c) => <td key={c.key} className={cn("px-4 py-2.5 align-top", c.cellClassName)}>{c.render ? c.render(row) : row[c.key] ?? "—"}</td>)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 text-xs text-slate-600">
        <span data-testid="table-pagination-summary">Showing {from} to {to} of {total.toLocaleString()} entries</span>
        <div className="flex items-center gap-2">
          {onPageSize && (
            <Select value={String(pageSize)} onValueChange={(v) => onPageSize(Number(v))}>
              <SelectTrigger className="h-8 w-[88px]" data-testid="table-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>{[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n} rows</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" className="h-8" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="table-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="font-mono" data-testid="table-page-indicator">{page} / {pages}</span>
          <Button variant="outline" size="sm" className="h-8" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="table-next-page"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}
